import * as React from "react";
// Action creators
import { ActionCreator, useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
// Constants
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
// Controls
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { MeetingPlanExpirationMessageBar } from "@ms/planner-presentational-controls/lib/local/meetingPlanExpirationMessageBar/MeetingPlanExpirationMessageBar";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { Temporal } from "temporal-polyfill-lite";
// Models
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { getPlanPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
// Utilities
import { DeepLinkNotificationChannel, DeepLinkNotificationType, LegacyDeepLinkQueryParameterKeys } from "@ms/uno-routing/lib/local/utilities/DeepLinkUtils";
import { parseDotNetTicksToInstant } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
const componentName = "MeetingPlanExpirationMessageBar";
/**
 * Container for meeting plan expiration message bar.
 */ export const MeetingPlanExpirationMessageBarContainer = ({ planId })=>{
    const { traceLogger, engagementLogger } = useLoggers();
    const [planStore, permissionsStore, userStore, modernGroupsStore, rosterStore, teamsChannelStore, todoStore, appContextStore, driveItemStore, dependencyStore, taskStore, routeStore] = useStores([
        Store.Plan,
        Store.Permissions,
        Store.User,
        Store.ModernGroups,
        Store.Roster,
        Store.TeamsChannel,
        Store.Todo,
        Store.AppContext,
        Store.DriveItem,
        Store.Dependency,
        Store.Tasks,
        Store.Route
    ]);
    const [planActionCreator] = useActionCreators([
        ActionCreator.Plan
    ]);
    const currentPlan = planStore.getPlan(planId).data;
    if (currentPlan?.expirationDateTime == null) {
        return null;
    }
    // Get query parameters from route to check for retention notification.
    // Legacy links include Type, Channel, and CreatedTime metadata for the notification.
    const queryParams = routeStore.getCurrentQueryParameters();
    const notificationType = queryParams[LegacyDeepLinkQueryParameterKeys.Type];
    const notificationChannel = queryParams[LegacyDeepLinkQueryParameterKeys.Channel];
    const notificationCreatedTime = queryParams[LegacyDeepLinkQueryParameterKeys.CreatedTime];
    // Recent notifications are still valid if they were created within the last 120 days.
    // A retained plan message should only display when the plan expiry is more than 30 days away.
    const isValidRetainedNotification = ()=>{
        if (!notificationCreatedTime || currentPlan.expirationDateTime == null) {
            return false;
        }
        const createdTime = parseDotNetTicksToInstant(notificationCreatedTime);
        if (createdTime == null) {
            return false;
        }
        const ageInDays = Temporal.Now.instant().since(createdTime).total({
            unit: "days"
        });
        if (ageInDays < 0 || ageInDays > 120) {
            return false;
        }
        const dayDelta = currentPlan.expirationDateTime.since(createdTime).total({
            unit: "days"
        });
        return dayDelta > 30;
    };
    const normalizedNotificationType = notificationType?.toLowerCase() ?? "";
    const normalizedNotificationChannel = notificationChannel?.toLowerCase() ?? "";
    const shouldShowRetainedMessage = normalizedNotificationType === DeepLinkNotificationType.PlanExpiry && normalizedNotificationChannel === DeepLinkNotificationChannel.OdspNotifyV2 && isValidRetainedNotification();
    const permissionUtilityStores = {
        permissionsStore,
        modernGroupsStore,
        rosterStore,
        userStore,
        teamsChannelStore,
        appContextStore,
        planStore,
        driveItemStore,
        todoStore,
        dependencyStore,
        taskStore
    };
    const planPermissions = getPlanPermissionsFromStores(currentPlan, permissionUtilityStores, traceLogger);
    if (!planPermissions.canUpdatePlanExpiry) {
        return null;
    }
    const onKeepPlanClicked = ()=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.KeepPlanButtonClicked, {
            planId
        });
        planActionCreator.extendPlanExpiry(planId);
    };
    const dismissKeepPlanFailure = ()=>{
        planActionCreator.resetExtendPlanExpiryOperationStatus(planId);
    };
    return /*#__PURE__*/ React.createElement(MeetingPlanExpirationMessageBar, {
        expiryDate: currentPlan.expirationDateTime,
        onKeepPlanClicked: onKeepPlanClicked,
        keepPlanOperationStatus: planStore.getExtendPlanExpiryOperationStatus(planId),
        onDismissKeepPlanFailure: dismissKeepPlanFailure,
        shouldShowRetainedMessage: shouldShowRetainedMessage
    });
};
